import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { 
  FaCalendarAlt,
  FaClock,
  FaUser,
  FaSearch,
  FaArrowRight,
  FaTag
} from 'react-icons/fa';

const AnimatedCard: React.FC<{
  children: React.ReactNode;
  delay?: number;
  className?: string;
}> = ({ children, delay = 0, className = '' }) => {
  const { ref, isVisible } = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

interface BlogPost {
  id: number;
  title: string;
  titleAr: string;
  excerpt: string;
  excerptAr: string;
  category: string;
  categoryAr: string;
  date: string;
  readTime: number;
  image: string;
  author: string;
}

const Blog: React.FC = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const blogPosts: BlogPost[] = [
    {
      id: 1,
      title: 'Complete Guide to Umrah: Step by Step',
      titleAr: 'دليل شامل للعمرة: خطوة بخطوة',
      excerpt: 'Learn everything you need to know about performing Umrah, from preparation to completion. Essential tips for a blessed journey.',
      excerptAr: 'تعلم كل ما تحتاج معرفته عن أداء العمرة، من التحضير إلى الإتمام. نصائح أساسية لرحلة مباركة.',
      category: 'umrah',
      categoryAr: 'العمرة',
      date: '2024-01-15',
      readTime: 8,
      image: 'umrah-guide',
      author: 'Ahmed Al-Mansouri'
    },
    {
      id: 2,
      title: 'Hajj 2024: Important Dates and Preparation',
      titleAr: 'الحج 2024: التواريخ المهمة والتحضير',
      excerpt: 'Everything you need to know about Hajj 2024, including important dates, preparation checklist, and what to expect.',
      excerptAr: 'كل ما تحتاج معرفته عن الحج 2024، بما في ذلك التواريخ المهمة وقائمة التحضير وما يمكن توقعه.',
      category: 'hajj',
      categoryAr: 'الحج',
      date: '2024-01-10',
      readTime: 12,
      image: 'hajj-2024',
      author: 'Sarah Johnson'
    },
    {
      id: 3,
      title: 'Best Ziyarat Sites in Saudi Arabia',
      titleAr: 'أفضل مواقع الزيارات في المملكة العربية السعودية',
      excerpt: 'Discover the most significant historical and religious sites to visit during your journey in Saudi Arabia.',
      excerptAr: 'اكتشف أهم المواقع التاريخية والدينية التي يمكن زيارتها خلال رحلتك في المملكة العربية السعودية.',
      category: 'ziyarat',
      categoryAr: 'الزيارات',
      date: '2024-01-08',
      readTime: 6,
      image: 'ziyarat-sites',
      author: 'Mohammed Al-Rashid'
    },
    {
      id: 4,
      title: 'Travel Tips for First-Time Visitors to Saudi Arabia',
      titleAr: 'نصائح السفر للزوار لأول مرة في المملكة العربية السعودية',
      excerpt: 'Essential travel tips and cultural guidelines for first-time visitors to ensure a smooth and respectful journey.',
      excerptAr: 'نصائح سفر أساسية وإرشادات ثقافية للزوار لأول مرة لضمان رحلة سلسة ومحترمة.',
      category: 'travel',
      categoryAr: 'نصائح السفر',
      date: '2024-01-05',
      readTime: 5,
      image: 'travel-tips',
      author: 'Fatima Al-Zahra'
    },
    {
      id: 5,
      title: 'Choosing the Right Vehicle for Group Travel',
      titleAr: 'اختيار المركبة المناسبة للسفر الجماعي',
      excerpt: 'A comprehensive guide to selecting the perfect vehicle for your group, whether it\'s a family trip or corporate travel.',
      excerptAr: 'دليل شامل لاختيار المركبة المثالية لمجموعتك، سواء كانت رحلة عائلية أو سفر للشركات.',
      category: 'vehicles',
      categoryAr: 'المركبات',
      date: '2024-01-03',
      readTime: 7,
      image: 'vehicle-guide',
      author: 'Khalid Al-Otaibi'
    },
    {
      id: 6,
      title: 'Expert Guide: Navigating Mecca and Medina',
      titleAr: 'دليل الخبير: التنقل في مكة والمدينة',
      excerpt: 'Professional tips and insider knowledge for navigating the holy cities of Mecca and Medina during your spiritual journey.',
      excerptAr: 'نصائح احترافية ومعرفة داخلية للتنقل في المدينتين المقدستين مكة والمدينة خلال رحلتك الروحية.',
      category: 'guide',
      categoryAr: 'دلائل',
      date: '2024-01-01',
      readTime: 10,
      image: 'mecca-medina',
      author: 'Aisha Al-Mutairi'
    },
  ];

  const categories = [
    { id: 'all', name: t('blog.allCategories'), nameAr: t('blog.allCategories') },
    { id: 'umrah', name: t('blog.categories.umrah'), nameAr: t('blog.categories.umrah') },
    { id: 'hajj', name: t('blog.categories.hajj'), nameAr: t('blog.categories.hajj') },
    { id: 'ziyarat', name: t('blog.categories.ziyarat'), nameAr: t('blog.categories.ziyarat') },
    { id: 'travel', name: t('blog.categories.travel'), nameAr: t('blog.categories.travel') },
    { id: 'vehicles', name: t('blog.categories.vehicles'), nameAr: t('blog.categories.vehicles') },
    { id: 'guide', name: t('blog.categories.guide'), nameAr: t('blog.categories.guide') },
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = isRTL
      ? post.titleAr.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerptAr.toLowerCase().includes(searchTerm.toLowerCase())
      : post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 text-white py-20">
        <div className="absolute inset-0 opacity-5 bg-geometric-pattern"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl lg:text-6xl font-bold mb-4">
              {t('blog.title')}
            </h1>
            <p className="text-2xl text-green-50 font-medium">
              {t('blog.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-light-green-100">
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative">
                {/* @ts-expect-error - react-icons compatibility with React 19 */}
                <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-light-green-500" size={20} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('blog.search')}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-lg focus:border-light-green-500 focus:outline-none text-lg"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-light-green-500 to-light-green-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isRTL ? category.nameAr : category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-2xl text-gray-600">{t('blog.noPosts')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, index) => (
                <AnimatedCard key={post.id} delay={index * 100}>
                  <article className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border-2 border-light-green-100 group">
                    {/* Image */}
                    <div className="h-64 bg-gradient-to-br from-light-green-400 via-light-green-500 to-saudi-green-500 relative overflow-hidden">
                      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-white text-light-green-600 px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                          {isRTL ? post.categoryAr : post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Meta Information */}
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                        <div className="flex items-center gap-2">
                          {/* @ts-expect-error - react-icons compatibility with React 19 */}
                          <FaCalendarAlt className="text-light-green-500" size={14} />
                          <span>{formatDate(post.date)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {/* @ts-expect-error - react-icons compatibility with React 19 */}
                          <FaClock className="text-light-green-500" size={14} />
                          <span>{post.readTime} {t('blog.readTime')}</span>
                        </div>
                      </div>

                      {/* Title */}
                      <h2 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-light-green-600 transition-colors">
                        {isRTL ? post.titleAr : post.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3">
                        {isRTL ? post.excerptAr : post.excerpt}
                      </p>

                      {/* Author and Read More */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          {/* @ts-expect-error - react-icons compatibility with React 19 */}
                          <FaUser className="text-light-green-500" size={14} />
                          <span>{post.author}</span>
                        </div>
                        <button className="flex items-center gap-2 text-light-green-600 font-semibold hover:text-light-green-700 transition-colors group-hover:gap-3">
                          {t('blog.readMore')}
                          {/* @ts-expect-error - react-icons compatibility with React 19 */}
                          <FaArrowRight className={`transition-transform ${isRTL ? 'rotate-180' : ''}`} size={14} />
                        </button>
                      </div>
                    </div>
                  </article>
                </AnimatedCard>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA Section */}
      <section className="py-20 bg-gradient-to-r from-light-green-400 via-light-green-500 to-saudi-green-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4">
            {language === 'en' ? 'Stay Updated' : 'ابق على اطلاع'}
          </h2>
          <p className="text-xl mb-8 text-green-50">
            {language === 'en' 
              ? 'Subscribe to our newsletter for the latest travel tips and news'
              : 'اشترك في نشرتنا الإخبارية للحصول على أحدث نصائح السفر والأخبار'}
          </p>
          <button className="px-10 py-4 bg-white text-light-green-600 rounded-lg font-bold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-xl">
            {language === 'en' ? 'Subscribe Now' : 'اشترك الآن'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Blog;
